import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCuentaShell } from "../pages/estilos/EstiloMinimal";
import {
  Loader2,
  Package,
  Pencil,
  Search,
  Sparkles,
  Upload,
} from "lucide-react";
import type { MockProductV2 } from "../pages/v2/mockData";
import { fetchUserProducts } from "../pages/v2/productsApi";
import { EVENTO_CATALOGO_EN_CAMINO } from "../lib/traerCatalogoAlConectar";
import CachedImg from "./CachedImg";
import ProductGridSkeleton from "./ProductGridSkeleton";
import { TarjetaSubirProducto } from "./TarjetaSubirProducto";
import { EditProductFlow } from "./EditProductFlow";
import { SheetModosProducto } from "./SheetModosProducto";
import { useImportacionDeProductos } from "../hooks/useImportacionDeProductos";
import { BarraDeImportacion } from "./BarraDeImportacion";
import { BotonDeImportacion } from "./BotonDeImportacion";

// Un producto está "afinándose" si se importó/creó hace poco y todavía no tiene
// categoría (`modelView`) — el back se la pone unos segundos después. Acotamos a
// "reciente" (por `createdAt`) para NO marcar productos viejos sin categorizar.
// En una importación masiva el análisis puede durar más que eso: mientras la importación
// analiza, lo que llegó desde que arrancó también cuenta (ADR-2026-09-29-importacion-masiva-front).
const RECENT_MS = 15 * 60 * 1000;
const isAfinando = (p: MockProductV2, analizandoDesde: number | null = null): boolean => {
  // Un importado que espera su análisis a pedido no se está analizando: se analiza cuando se use
  // (ADR-2026-10-05-analisis-a-pedido).
  if (p.modelView || !p.createdAt || p.analisisPendiente) return false;
  const creado = new Date(p.createdAt).getTime();
  if (Date.now() - creado < RECENT_MS) return true;
  // Un minuto de margen por la diferencia de reloj entre el navegador y la base.
  return analizandoDesde !== null && creado >= analizandoDesde - 60_000;
};
// "Pendiente" para el polling: sin imagen aún, o afinándose.
const isPending = (p: MockProductV2): boolean => !p.imageUrl || isAfinando(p);
/** Durante una importación masiva la grilla se refresca como mucho cada tanto (trae todo el catálogo). */
const REFRESCO_POR_IMPORTACION_MS = 15_000;

// La grilla ocupa todo el ancho desde ADR-2026-10-02-mi-marca-cabecera-de-identidad
// (antes compartía la fila con una columna de 440px): por eso llega a cuatro columnas.
const GRILLA = "grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5 sm:gap-x-4 sm:gap-y-6";

/**
 * Tab "Mis productos" de la pantalla Mi marca: barra de búsqueda + botón para
 * agregar producto (mismo flujo/UI que seleccionar productos) + grilla de los
 * productos de la marca activa.
 */
export const MiMarcaProductsTab = ({
  companyId,
  brandId,
  marcaResuelta,
  rubro = null,
}: {
  companyId: string | null;
  brandId: string | null;
  /**
   * `false` mientras `useBrandProfile` no terminó de resolver la marca activa —
   * ADR-2026-08-04-los-productos-esperan-a-saber-de-que-marca-son.
   *
   * NO es un detalle de presentación: `brandId` en `null` significa "todas las marcas"
   * para `fetchUserProducts`, y es indistinguible de "todavía no sé cuál es". Sin esta
   * prop, la grilla arranca pidiendo el catálogo entero de la empresa.
   */
  marcaResuelta: boolean;
  /** Rubro de la marca activa — decide si el sheet de modos ofrece Plantillas. */
  rubro?: string | null;
}) => {
  // Subir productos es una pantalla de Mi marca, no un panel encima de la grilla —
  // ADR-2026-10-13-subir-productos-es-una-pantalla. Al volver, la grilla se monta de
  // nuevo y trae lo que se creó (y sigue a los que todavía se están procesando). Se va
  // con el fundido del shell, como un cambio de sección
  // (ADR-2026-10-13-subir-productos-mismo-ancho-y-entrada-suave).
  const navigate = useNavigate();
  const { conFadeDeSalida } = useCuentaShell();
  const irASubir = () => conFadeDeSalida(() => navigate("/mi-marca/productos/nuevo"));
  const [products, setProducts] = useState<MockProductV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // Producto en edición (abre el panel "Editar producto" — VariantForm).
  const [editingProduct, setEditingProduct] = useState<MockProductV2 | null>(
    null,
  );
  // Producto tocado en la grilla: abre el sheet de modos
  // (ADR-2026-08-04-desde-un-producto-se-elige-el-modo).
  const [productoParaGenerar, setProductoParaGenerar] =
    useState<MockProductV2 | null>(null);
  const pollRef = useRef<number | null>(null);
  // `true` mientras corre el polling largo de un import de otro lado (ver `startPolling`).
  const pollLargoRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollLargoRef.current = false;
  }, []);

  /**
   * Número de la última consulta pedida —
   * ADR-2026-08-04-los-productos-esperan-a-saber-de-que-marca-son.
   *
   * Cambiar de marca dispara una consulta nueva sin cancelar la anterior, y no hay
   * garantía de que vuelvan en orden: si la vieja contesta última, pinta la grilla de
   * la marca anterior. Cada llamada se queda con su número y sólo escribe si sigue
   * siendo la última.
   */
  const consultaRef = useRef(0);

  // Trae los productos y los setea, SIN tocar el skeleton (para refrescos
  // silenciosos del polling). `fetchUserProducts` maneja ambos casos: con marca
  // activa trae los de esa marca + los sin marca (`brand_id null`); SIN marca
  // activa trae todos los de la empresa. Ver [[productos-huerfanos-brand-null]].
  const fetchAndSet = useCallback(async (): Promise<MockProductV2[]> => {
    if (!companyId) return [];
    const mia = ++consultaRef.current;
    const p = await fetchUserProducts(companyId, brandId);
    // Llegó tarde: ya hay otra consulta en vuelo (o resuelta) para otra marca. Se
    // devuelve igual —quien llamó decide qué hacer— pero no se pinta.
    if (mia !== consultaRef.current) return p;
    setProducts(p);
    return p;
  }, [companyId, brandId]);

  // Polling acotado: re-consulta cada 5s mientras haya productos "pendientes"
  // (sin imagen aún, o recién importados sin categorizar → badge "afinando"),
  // y corta cuando ya están todos o al agotar la ventana (~90s). SIN tocar la
  // base — puro re-fetch del front.
  //
  // `opts` es para un import que corre en OTRO lado (conectar la tienda): ahí el
  // primer vistazo puede no traer nada todavía —la cola del worker puede estar
  // cargada— y "nada pendiente" no quiere decir "terminó". `listo` decide cuándo
  // cortar y `maxTicks` estira la ventana (review Greptile #154).
  const startPolling = useCallback(
    (opts?: { listo?: (p: MockProductV2[]) => boolean; maxTicks?: number }) => {
      stopPolling();
      let ticks = 0;
      const MAX_TICKS = opts?.maxTicks ?? 18; // ~90s a 5s
      const listo = opts?.listo ?? ((p: MockProductV2[]) => !p.some(isPending));
      pollLargoRef.current = !!opts;
      pollRef.current = window.setInterval(async () => {
        ticks += 1;
        const p = await fetchAndSet();
        if (listo(p) || ticks >= MAX_TICKS) stopPolling();
      }, 5000);
    },
    [fetchAndSet, stopPolling],
  );

  const load = useCallback(async () => {
    // Sin companyId no hay nada que traer; apagamos el loading para no dejar el
    // skeleton colgado.
    if (!companyId) {
      setLoading(false);
      return;
    }
    // Todavía no se sabe cuál es la marca activa: se espera en el esqueleto
    // (ADR-2026-08-04-los-productos-esperan-a-saber-de-que-marca-son). Pedir ahora
    // traería el catálogo de TODAS las marcas —`brandId` null es "sin filtro"— y esa
    // grilla equivocada se ve hasta que conteste la consulta buena. `load` vuelve a
    // correr cuando la marca resuelve, porque está en sus deps.
    if (!marcaResuelta) {
      setLoading(true);
      return;
    }
    setLoading(true);
    try {
      const p = await fetchAndSet();
      // Si al entrar ya hay pendientes (import en curso), arrancar el polling.
      if (p.some(isPending)) startPolling();
    } finally {
      setLoading(false);
    }
  }, [companyId, marcaResuelta, fetchAndSet, startPolling]);

  useEffect(() => {
    load();
    // Al cambiar de marca o desmontar, cortar cualquier polling en curso.
    return stopPolling;
  }, [load, stopPolling]);

  // Importación masiva (el botón de refresh y la rayita —
  // ADR-2026-09-30-el-detalle-de-la-importacion-vive-en-el-boton): los productos nuevos aparecen solos.
  // Con un tope de un refresco cada 15 s, y siempre uno al final.
  const importacion = useImportacionDeProductos({
    companyId,
    brandId,
    listo: marcaResuelta,
  });
  const { analizandoDesde, firma } = importacion;
  const ultimoRefrescoRef = useRef(0);
  const refrescoPendienteRef = useRef<number | null>(null);
  const firmaAnteriorRef = useRef<string | null>(null);
  useEffect(() => {
    const antes = firmaAnteriorRef.current;
    firmaAnteriorRef.current = firma;
    // La primera vez (o sin nada que haya cambiado) no hay nada nuevo que traer: `load` ya trajo.
    if (antes === null || antes === firma) return;
    if (refrescoPendienteRef.current !== null) return; // ya hay uno programado
    const espera = Math.max(0, ultimoRefrescoRef.current + REFRESCO_POR_IMPORTACION_MS - Date.now());
    refrescoPendienteRef.current = window.setTimeout(() => {
      refrescoPendienteRef.current = null;
      ultimoRefrescoRef.current = Date.now();
      void fetchAndSet();
    }, espera);
  }, [firma, fetchAndSet]);
  useEffect(
    () => () => {
      if (refrescoPendienteRef.current !== null) window.clearTimeout(refrescoPendienteRef.current);
      refrescoPendienteRef.current = null;
    },
    [],
  );

  // Conectar la tienda (botón de Mi marca, que es otro componente) trae el catálogo:
  // cuando sale, se refresca y se sigue mirando hasta que llegue algo nuevo y no quede
  // nada afinándose, con una ventana más larga que la de un import propio
  // (ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo).
  useEffect(() => {
    const alTraer = (e: Event) => {
      const { desde } = (e as CustomEvent<{ desde?: number }>).detail ?? {};
      // Margen por la diferencia de reloj entre el navegador y la base.
      const umbral = (desde ?? Date.now()) - 60_000;
      const llegoAlgo = (p: MockProductV2[]) =>
        p.some((x) => !!x.createdAt && new Date(x.createdAt).getTime() >= umbral);
      void fetchAndSet();
      startPolling({
        listo: (p) => llegoAlgo(p) && !p.some(isPending),
        maxTicks: 36, // ~3 min
      });
    };
    window.addEventListener(EVENTO_CATALOGO_EN_CAMINO, alTraer);
    return () => window.removeEventListener(EVENTO_CATALOGO_EN_CAMINO, alTraer);
  }, [fetchAndSet, startPolling]);

  const q = query.toLowerCase().trim();
  const filtered = q
    ? products.filter((p) => (p.name || "").toLowerCase().includes(q))
    : products;

  return (
    <div>
      {/* Barra de búsqueda — ADR-2026-10-02-mi-marca-cabecera-de-identidad. Píldora,
          como el selector de tabs de arriba, con el botón de buscar productos nuevos de la
          importación al lado (ADR-2026-09-30-el-detalle-de-la-importacion-vive-en-el-boton).
          "Agregar producto" ya no va acá: es la primera tarjeta de la grilla
          (ADR-2026-10-05-mi-marca-agregar-como-primera-tarjeta). El progreso de la
          importación flota debajo (absoluto): no mueve la grilla. */}
      <div className="relative mb-5 flex items-center gap-3">
        <div className="relative flex-1">
          <label htmlFor="mimarca-search-productos" className="sr-only">
            Buscar productos
          </label>
          <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-black/35" />
          <input
            id="mimarca-search-productos"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar productos"
            className="w-full pl-10 pr-4 h-[var(--btn-h)] rounded-full border border-black/[0.08] bg-white text-[length:var(--text-body)] text-[#16181D] placeholder:text-black/35 shadow-[0_1px_2px_rgba(16,24,40,0.04)] focus:border-brand-primary focus:ring-4 focus:ring-[#EBEFFE] outline-none transition-shadow"
          />
        </div>
        <BotonDeImportacion
          brandId={brandId}
          importacion={importacion}
          onRepetidoResuelto={() => void fetchAndSet()}
        />
        <BarraDeImportacion activa={importacion.activa} />
      </div>

      {loading ? (
        <ProductGridSkeleton
          count={8}
          gridClassName={GRILLA}
          tileClassName="rounded-2xl"
          withLabel
        />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center rounded-3xl border border-dashed border-black/[0.12] px-6 py-16">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_0_0_1px_rgba(16,24,40,0.06)]">
            <Package className="h-5 w-5 text-black/45" strokeWidth={1.75} />
          </div>
          <p className="text-[15px] font-semibold text-[#16181D]">
            {query ? "No encontramos ese producto" : "Todavía no hay productos"}
          </p>
          <p className="mt-1 max-w-[340px] text-[13.5px] leading-snug text-black/50">
            {query
              ? "Probá con otra palabra o revisá cómo está escrito."
              : "Cargá tu primer producto y empezá a crear fotos y videos con él."}
          </p>
          {!query && (
            <button
              type="button"
              onClick={irASubir}
              className="mt-5 inline-flex h-10 items-center gap-2 rounded-full bg-brand-primary px-5 text-[14px] font-medium text-white transition-colors hover:bg-brand-primary-hover"
            >
              <Upload className="h-4 w-4" />
              Subir nuevo producto
            </button>
          )}
        </div>
      ) : (
        <>
          {/* La cantidad como título de la grilla —
              ADR-2026-10-02-mi-marca-contador-como-titulo. Al lado del buscador se leía
              como un dato suelto; arriba de las fotos dice qué es lo que sigue. Con una
              búsqueda escrita cuenta los que quedaron ("3 de 71"). */}
          <h2 className="mb-3 px-0.5 text-[15px] font-semibold text-[#16181D]">
            {q
              ? `${filtered.length} de ${products.length} productos`
              : `${products.length} ${products.length === 1 ? "producto" : "productos"}`}
          </h2>
          <div className={GRILLA}>
            {/* Subir un producto nuevo: la primera tarjeta de la grilla, como en
                Automático (`renderTileSubir` de `PickProductPanel`) —
                ADR-2026-10-05-mi-marca-agregar-como-primera-tarjeta. Cuadrada como las
                fotos y con el texto adentro, así la fila no cambia de alto. Con una
                búsqueda escrita no va: la grilla son sólo los resultados.

                Lo visual vive en `TarjetaSubirProducto`: dos productos reales del rubro y
                el lugar vacío para el tuyo —
                ADR-2026-10-13-mi-marca-tarjeta-subir-con-productos-reales. */}
            {!q && (
              <div className="flex flex-col motion-safe:animate-[aparecer_0.45s_ease-out_both]">
                <TarjetaSubirProducto rubro={rubro} onClick={irASubir} />
              </div>
            )}
            {filtered.map((p, i) => (
              // Entran escalonadas (35ms entre una y otra, tope en la 12ª para que
              // ninguna espere de más) — ADR-2026-10-02-mi-marca-animaciones.
              <div
                key={p.id}
                className="group flex flex-col motion-safe:animate-[aparecer_0.45s_ease-out_both]"
                style={{ animationDelay: `${Math.min(i + (q ? 0 : 1), 11) * 35}ms` }}
              >
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-black/[0.04] ring-1 ring-inset ring-black/[0.04] transition-shadow duration-300 sm:group-hover:shadow-[0_12px_32px_-12px_rgba(16,24,40,0.35)]">
                  {p.imageUrl ? (
                    <CachedImg
                      src={p.imageUrl}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out sm:group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-black/25">
                      <Package className="w-6 h-6" strokeWidth={1.5} />
                    </div>
                  )}
                  {/* Al pasar el mouse: degradé + "Crear con este producto", que es lo
                      que hace el click (abre el sheet de modos). Sin esto la tarjeta no
                      decía que era tocable ni para qué. Sólo con hover: en el teléfono
                      el tap ya es el gesto obvio. */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 hidden sm:flex h-1/2 items-end bg-gradient-to-t from-black/55 via-black/15 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  >
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white">
                      <Sparkles className="h-3.5 w-3.5" />
                      Crear con este producto
                    </span>
                  </div>
                  {isAfinando(p, analizandoDesde) && (
                    <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Analizando producto
                    </span>
                  )}
                  {/* Capa de click sobre la miniatura: abre el sheet de modos para
                      generar con ESE producto
                      (ADR-2026-08-04-desde-un-producto-se-elige-el-modo). Va como
                      `<button>` absoluto y no envolviendo la tarjeta porque adentro
                      hay otro botón —el lápiz—, y un botón dentro de otro es HTML
                      inválido: el navegador desarma el anidado y el click deja de
                      llegar donde corresponde. El lápiz va con `z-10`, por encima. */}
                  <button
                    type="button"
                    onClick={() => setProductoParaGenerar(p)}
                    aria-label={`Generar con ${p.name}`}
                    className="absolute inset-0 z-[1] cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary"
                  />
                  {/* Editar: lápiz arriba a la derecha (hover en desktop, siempre en
                      mobile). Arriba y no abajo: abajo ahora va el "Crear con este
                      producto" del hover. En mobile, sin círculo: el ícono solo, blanco
                      y con sombra para despegarse de la foto — siempre visible, tiene
                      que molestar poco (ADR-2026-10-05-mi-marca-lapiz-sin-fondo-en-mobile). */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingProduct(p);
                    }}
                    aria-label="Editar producto"
                    title="Editar producto"
                    className="absolute top-2 right-2 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full text-white transition-all active:scale-90 sm:bg-white/95 sm:text-black/75 sm:shadow-md sm:ring-1 sm:ring-black/[0.06] sm:backdrop-blur-md sm:hover:bg-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-visible:opacity-100"
                  >
                    <Pencil className="w-[17px] h-[17px] drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)] sm:w-3.5 sm:h-3.5 sm:drop-shadow-none" />
                  </button>
                </div>
                <p className="mt-2 px-0.5 text-[13px] font-medium text-[#16181D] leading-tight line-clamp-1">
                  {p.name}
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Sheet de modos: el mismo drawer del picker de Formato, con los modos del
          inicio (candado incluido) y Plantillas si la marca es de Moda. */}
      <SheetModosProducto
        producto={productoParaGenerar}
        rubro={rubro}
        onClose={() => setProductoParaGenerar(null)}
      />

      {/* Panel "Editar producto" (VariantForm) — desktop = panel lateral,
          mobile/PWA = bottom-sheet (lo resuelve el propio VariantForm). */}
      {editingProduct && (
        <EditProductFlow
          companyId={companyId}
          product={{ id: editingProduct.id, name: editingProduct.name }}
          onClose={() => setEditingProduct(null)}
          onSaved={fetchAndSet}
        />
      )}
    </div>
  );
};
